import {
  Box,
  Icon,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Text,
  Button,
  Heading,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  useDisclosure,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { FiHelpCircle } from 'react-icons/fi';

import { useIsDatasetComparisonEnabled } from 'components/shared/Compare';
import useAllIndicators from 'hooks/data/datasets/lcia/useAllIndicators';
import { AccessResourceKey } from 'hooks/data/files/types';
import { Activity } from 'hooks/data/search/types';
import useAccess from 'hooks/useAccess';
import useVersionInfo from 'hooks/useVersionInfo';
import { newerVersions } from 'pages/Search/utils';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { buildDefaultSelectedLciaMethod, selectedMethodToString } from '../../SearchPreferencesModalContent/utils';

import DatasetRowGroup from './DatasetRowGroup';

type Props = {
  activity: Activity;
  resultPosition: number;
};

const ActivitySearchResultTable = ({ activity, resultPosition }: Props) => {
  const { ver, system_model } = useVersionInfo();

  const { data: accessData } = useAccess();
  const hasScoresAccess = checkAccessToResource(accessData, AccessResourceKey.SCORES, ver);
  const { isOpen, onClose, onToggle } = useDisclosure();
  const isCompareEnabled = useIsDatasetComparisonEnabled();

  const { data: indicators } = useAllIndicators();
  const selectedMethod = indicators && buildDefaultSelectedLciaMethod(indicators, ver, system_model);

  const thGray = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconColor = useColorModeValue('green.500', 'green.300');

  const onClosePopover = () => {
    mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLOSED, { info: 'Impact Score' });
    onClose();
  };

  return (
    <TableContainer whiteSpace={'auto'}>
      <Table variant="simple" fontSize={'md'} size="sm">
        <Thead borderColor={borderColor}>
          <Tr>
            {system_model !== 'undefined' && (
              <Th borderColor={borderColor} color={thGray} maxW={{ base: '100%', xl: '400px' }} py={2}>
                Reference Product
              </Th>
            )}
            <Th borderColor={borderColor} color={thGray}>
              Geography
            </Th>
            {hasScoresAccess && system_model !== 'undefined' && newerVersions.includes(ver) && (
              <Th justifyContent={'flex-end'} borderColor={borderColor} color={thGray} whiteSpace={'nowrap'} isNumeric pr={14}>
                <Text as="span">Impact Score</Text>
                <Popover isOpen={isOpen} onClose={onClosePopover} strategy="fixed">
                  <PopoverTrigger>
                    <Box p={0} verticalAlign={'middle'} ml={2} display={'inline-block'} cursor={'pointer'}>
                      <Icon
                        mt="2px"
                        as={FiHelpCircle}
                        color={iconColor}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLICKED, { info: 'Impact Score' });
                          onToggle();
                        }}
                      />
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent px={6} py={5}>
                    <PopoverArrow />
                    <PopoverHeader borderBottom="none" p={0}>
                      <Heading size="sm">Impact Score</Heading>
                    </PopoverHeader>
                    <PopoverBody p={0} mt={4}>
                      <Stack spacing={3}>
                        <Text>
                          The environmental impact score for this dataset, calculated using a specific method and indicator
                        </Text>
                        <Text>
                          Currently showing:{' '}
                          <Text as="span" fontWeight="bold">
                            {selectedMethod && selectedMethodToString(selectedMethod)}
                          </Text>
                          — this is the carbon footprint in kg CO2-equivalents.
                        </Text>
                        <Text>
                          You can change which impact you want to see (like water use or toxicity) in the "Preferences" control
                          on this page.
                        </Text>
                      </Stack>
                    </PopoverBody>
                    <Stack flexDir="row" justify="flex-end" mt={4} spacing={2}>
                      <Button width={{ base: '100%', md: 'auto' }} onClick={onClose} variant="ghost">
                        Close
                      </Button>
                      <Button
                        width={{ base: '100%', md: 'auto' }}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.LEARN_MORE_INFO_POPOVER_CLICKED, { info: 'Impact Score' });
                          onClose();
                        }}
                        as={Link}
                        isExternal
                        href={'https://support.ecoinvent.org/lcia-results'}
                        sx={{ '&:hover': { textDecoration: 'none' } }}
                      >
                        Learn More
                      </Button>
                    </Stack>
                  </PopoverContent>
                </Popover>
              </Th>
            )}
            <Th w={isCompareEnabled ? '220px' : '150px'} borderColor={borderColor}>
              Dataset
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {activity.products.map((product, index) => (
            <DatasetRowGroup
              referenceProduct={product}
              key={index}
              isLastGroup={index === activity.products.length - 1}
              resultPosition={resultPosition}
              activityName={activity.name}
              activityType={activity.activity_type}
              sectors={activity.sectors}
              timePeriod={activity.time_period}
            />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default ActivitySearchResultTable;
